import { defineStore } from 'pinia'
import api from '@/services/api'
import { locationService } from '@/services/locationService'

export const useCartStore = defineStore('cart', {
  state: () => ({
    items: [],
    isOpen: false
  }),

  getters: {
    totalItems: (state) => state.items.length,
    subtotal: (state) => {
      // Disregards items with price -1 in the calculation (On request)
      return state.items.reduce((sum, item) => {
        const itemPrice = Number(item.price)
        if (isNaN(itemPrice) || itemPrice < 0) {
          return sum // Ignores items with price -1
        }
        return sum + (itemPrice * item.quantity)
      }, 0)
    },
    itemCount: (state) => state.items.reduce((total, item) => total + item.quantity, 0),
    total: (state) => {
      // Disregards items with price -1 in the calculation (On request)
      const subtotalValue = state.items.reduce((sum, item) => {
        const itemPrice = Number(item.price)
        if (isNaN(itemPrice) || itemPrice < 0) {
          return sum // Ignores items with price -1
        }
        return sum + (itemPrice * item.quantity)
      }, 0)
      const shippingValue = parseFloat(state.shipping || 0)
      const taxesValue = parseFloat(state.taxes || 0)
      return (subtotalValue + shippingValue + taxesValue).toFixed(2)
    }
  },

  actions: {
    async loadCartFromStorage(userId, clearOnLoad = false) {
      // ============================================================================
      // CURRENT VERSION (v1.0): Cart always starts empty ONLY after login
      // ============================================================================
      // This version was configured to always start the cart empty after login,
      // not loading items from previous sessions. This was done to simplify
      // the behavior in this version of the system.
      //
      // The `clearOnLoad` parameter should be `true` only when called during login.
      // In other situations (navigation, normal initialization), it should be `false` or omitted.
      //
      // To revert to the full behavior (load cart from database/localStorage):
      // 1. Comment out the "CURRENT VERSION" block below
      // 2. Uncomment the "ORIGINAL CODE" block further down
      // ============================================================================

      // If clearOnLoad is true (only on login), clears the cart
      if (clearOnLoad) {
        try {
          // Clears the cart from the state
          this.items = []

          // Clears the user's localStorage
          localStorage.removeItem(`cart_${userId}`)

          // Clears the cart in the database as well
          try {
            await api.delete('/cart')
            console.log('Cart cleared from database on login')
          } catch (error) {
            console.error('Error clearing cart from database on login:', error)
            // Continues even if clearing in the database fails
          }

          // Removes guest cart if it exists
          localStorage.removeItem('cart_guest')
          
          console.log('Cart initialized as empty after login')
          return []
        } catch (error) {
          console.error('Error initializing empty cart:', error)
          this.items = []
          return []
        }
      }
      
      // If clearOnLoad is false (normal navigation), loads from storage normally
      // But since we are on v1.0, we will keep the behavior of not loading from the database
      // only loading from localStorage if it exists (to keep items from the current session)
      try {
        // Loads from localStorage only (not from the database in v1.0)
        const localCart = localStorage.getItem(`cart_${userId}`)
        if (localCart) {
          const cartItems = JSON.parse(localCart)
          const groupedItems = this.groupCartItems(cartItems)
          this.items = groupedItems
          return groupedItems
        }
        
        // If it is not in localStorage, returns empty
        this.items = []
        return []
      } catch (error) {
        console.error('Error loading cart from storage:', error)
        this.items = []
        return []
      }

      // ============================================================================
      // ORIGINAL CODE (full behavior) - COMMENTED OUT FOR CURRENT VERSION
      // ============================================================================
      // To reactivate this behavior, comment out the block above and uncomment this one:
      /*
      try {
        // Checks if a guest cart exists
        const guestCart = localStorage.getItem('cart_guest')
        const guestCartItems = guestCart ? JSON.parse(guestCart) : []

        // ALWAYS fetches first from the database to have the source of truth
        let dbCartItems = []
        try {
          const response = await api.get(`/cart/${userId}`)
          if (response.data && response.data.items && response.data.items.length > 0) {
            dbCartItems = response.data.items.map(item => ({
              id: item.productId,
              quantity: item.quantity,
              price: item.price,
              name: item.name,
              image: item.image,
              color: item.color,
              size: item.size,
              weight: item.weight,
              unit: item.unit,
              foxpro_code: item.foxpro_code,
              description: item.description || null,
              lpor: item.lpor || false
            }))
          }
        } catch (error) {
          console.error('Error fetching cart from database:', error)
        }

        // If the database is empty, clears the localStorage as well to ensure consistency
        if (dbCartItems.length === 0) {
          localStorage.removeItem(`cart_${userId}`)
          this.items = []
          // If there is a guest cart, keeps only it
          if (guestCartItems.length > 0) {
            const groupedItems = this.groupCartItems(guestCartItems)
            this.items = groupedItems
            localStorage.setItem(`cart_${userId}`, JSON.stringify(groupedItems))
            localStorage.removeItem('cart_guest')
            // Synchronizes the guest cart with the backend
            await this.syncCartWithBackend()
            return groupedItems
          }
          return []
        }

        // If the database has items, uses them as the source of truth
        // Merges with the guest cart if it exists
        let finalCartItems = []
        if (guestCartItems.length > 0) {
          // Combines database items with guest cart items
          const allItems = [...dbCartItems, ...guestCartItems]
          finalCartItems = this.groupCartItems(allItems)
        } else {
          finalCartItems = this.groupCartItems(dbCartItems)
        }

        // Updates the state and localStorage with the database data (source of truth)
        this.items = finalCartItems
        localStorage.setItem(`cart_${userId}`, JSON.stringify(finalCartItems))
        localStorage.removeItem('cart_guest')

        // If there was a merge with the guest cart, synchronizes with the backend
        if (guestCartItems.length > 0) {
          await this.syncCartWithBackend()
        }

        return finalCartItems
      } catch (error) {
        console.error('Error loading cart:', error)
        // In case of error, tries to load from localStorage as fallback
        const localCart = localStorage.getItem(`cart_${userId}`)
        if (localCart) {
          const cartItems = JSON.parse(localCart)
          const groupedItems = this.groupCartItems(cartItems)
          this.items = groupedItems
          return groupedItems
        }
        this.items = []
        return []
      }
      */
      // ============================================================================
    },

    saveCartToStorage(userId) {
      if (userId) {
        localStorage.setItem(`cart_${userId}`, JSON.stringify(this.items))
      }
    },

    toggleCart() {
      this.isOpen = !this.isOpen
    },

    closeCart() {
      this.isOpen = false
    },

    // Helper function to save to localStorage
    saveToLocalStorage() {
      const userId = JSON.parse(localStorage.getItem('user'))?.id
      const storageKey = userId ? `cart_${userId}` : 'cart_guest'
      localStorage.setItem(storageKey, JSON.stringify(this.items))
    },

    async addItem(item) {
      // Checks if there is an item with the same ID and the same characteristics
      const existingItemIndex = this.items.findIndex(i =>
        i.id === item.id &&
        i.color === item.color &&
        i.size === item.size &&
        i.weight === item.weight &&
        i.unit === item.unit
      )

      if (existingItemIndex > -1) {
        // If it found an item with the same characteristics, just increments the quantity
        this.items[existingItemIndex].quantity += item.quantity
      } else {
        // If it did not find one, adds it as a new item
        this.items.push(item)
      }

      this.saveToLocalStorage()

      // Synchronizes with the backend to get complete product data
      const userId = JSON.parse(localStorage.getItem('user'))?.id
      if (userId) {
        try {
          await this.syncCartWithBackend()
        } catch (error) {
          console.error('Error syncing cart after adding item:', error)
        }
      }
    },

    async removeItem(index) {
      this.items.splice(index, 1)
      this.saveToLocalStorage()
      
      // Synchronizes with the backend if there is a logged-in user
      const userId = JSON.parse(localStorage.getItem('user'))?.id
      if (userId) {
        try {
          await this.syncCartWithBackend()
        } catch (error) {
          console.error('Error syncing cart after removing item:', error)
        }
      }
    },

    async updateQuantity(index, quantity) {
      if (quantity > 0) {
        this.items[index].quantity = quantity
        this.saveToLocalStorage()
        
        // Synchronizes with the backend if there is a logged-in user
        const userId = JSON.parse(localStorage.getItem('user'))?.id
        if (userId) {
          try {
            await this.syncCartWithBackend()
          } catch (error) {
            console.error('Error syncing cart after updating quantity:', error)
          }
        }
      }
    },

    $reset() {
      this.items = []
      this.isOpen = false
      // Does not save to localStorage on reset, since it may be called during logout
      // The localStorage will be cleared explicitly when necessary
    },

    async clearCart() {
      const userId = JSON.parse(localStorage.getItem('user'))?.id
      
      // Clears the state first
      this.items = []
      this.isOpen = false

      if (userId) {
        // Clears the localStorage immediately
        localStorage.removeItem(`cart_${userId}`)
        
        try {
          // Waits for the clearing in the database
          await api.delete('/cart')
          console.log('Cart cleared successfully from database')
          
          // Ensures the localStorage is clear after API success
          localStorage.removeItem(`cart_${userId}`)
        } catch (error) {
          console.error('Error clearing cart from database:', error)
          // Even if the API fails, keeps the localStorage clear
          // to avoid old data being loaded
          localStorage.removeItem(`cart_${userId}`)
          throw error // Propagates the error so the caller knows there was a problem
        }
      } else {
        // For a guest, just clears the localStorage
        localStorage.removeItem('cart_guest')
      }
    },

    async syncCartWithBackend() {
      try {
        const userId = JSON.parse(localStorage.getItem('user'))?.id;
        const selectedLocation = locationService.getSelectedLocation();

        if (!userId) {
          console.log('No user ID, skipping cart sync');
          return;
        }

        // If the cart is empty, clears it in the backend as well
        if (this.items.length === 0) {
          try {
            await api.delete('/cart');
            localStorage.removeItem(`cart_${userId}`);
            console.log('Cart synced: empty cart cleared from backend');
          } catch (error) {
            console.error('Error clearing empty cart from backend:', error);
          }
          return;
        }

        // Synchronizes items with the backend
        const response = await api.post('/cart/sync', {
          items: this.items.map(item => ({
            productId: item.id,
            quantity: item.quantity,
            color: item.color,
            size: item.size,
            weight: item.weight,
            unit: item.unit,
            price: item.price,  // Sends the price already calculated in the frontend
            locationId: selectedLocation?.id || null  // Adds locationId for dynamic price calculation
          }))
        });

        // Updates the state and localStorage with the API response (source of truth)
        if (response.data && response.data.items) {
          const apiItems = response.data.items.map(item => ({
            id: item.productId,
            quantity: item.quantity,
            price: item.price,
            name: item.name,
            image: item.image,
            color: item.color,
            size: item.size,
            weight: item.weight,
            unit: item.unit,
            foxpro_code: item.foxpro_code,
            description: item.description || null,
            lpor: item.lpor || false
          }));

          // Groups the items returned from the API to avoid duplicates
          const groupedItems = this.groupCartItems(apiItems);

          // Updates the state and localStorage with the backend data
          this.items = groupedItems;
          localStorage.setItem(`cart_${userId}`, JSON.stringify(groupedItems));
          console.log('Cart synced successfully with backend:', groupedItems.length, 'items');
        } else {
          // If the API returned empty, clears the state as well
          this.items = [];
          localStorage.removeItem(`cart_${userId}`);
          console.log('Cart synced: backend returned empty cart');
        }
      } catch (error) {
        console.error('Error syncing cart:', error);
        throw error;
      }
    },

    /**
     * Recalculates the prices of the cart items based on the new locationId
     * Keeps the cart items, only updates the prices
     */
    async recalculatePrices(newLocationId) {
      try {
        const userId = JSON.parse(localStorage.getItem('user'))?.id;
        
        if (!userId) {
          console.warn('Cannot recalculate prices: user not logged in');
          return;
        }

        if (this.items.length === 0) {
          console.log('Cart is empty, no prices to recalculate');
          return;
        }

        console.log(`🔄 Recalculando preços do carrinho para locationId: ${newLocationId}`);

        // Calls the backend endpoint to recalculate prices
        const response = await api.post('/cart/recalculate-prices', {
          locationId: newLocationId
        });

        // Updates the state and localStorage with the new prices
        if (response.data && response.data.items) {
          const apiItems = response.data.items.map(item => ({
            id: item.productId,
            quantity: item.quantity,
            price: item.price,
            name: item.name,
            image: item.image,
            color: item.color,
            size: item.size,
            weight: item.weight,
            unit: item.unit,
            foxpro_code: item.foxpro_code,
            description: item.description || null,
            lpor: item.lpor || false
          }));

          // Keeps the same grouping structure
          const groupedItems = this.groupCartItems(apiItems);

          this.items = groupedItems;
          localStorage.setItem(`cart_${userId}`, JSON.stringify(groupedItems));
          
          console.log(`✅ Preços recalculados com sucesso para ${groupedItems.length} itens`);
        }
      } catch (error) {
        console.error('Error recalculating cart prices:', error);
        throw error;
      }
    },

    // Helper function to group cart items
    groupCartItems(items) {
      const groupedMap = new Map();

      items.forEach(item => {
        // Creates a unique key based on the product ID and characteristics
        const key = `${item.id}_${item.color || ''}_${item.size || ''}_${item.weight || ''}_${item.unit || ''}`;

        if (groupedMap.has(key)) {
          // If an item with the same characteristics already exists, sums the quantities
          const existingItem = groupedMap.get(key);
          existingItem.quantity += item.quantity;
        } else {
          // If it does not exist, adds the new item
          groupedMap.set(key, { ...item });
        }
      });

      return Array.from(groupedMap.values());
    }
  }
})



































